import ChatFilterControl, { ChatFilterChips } from './ChatFilterControl.tsx';
import { ConversationTabs, type AssignmentFilter, type ConversationState } from './ContactAttendance.tsx';
/**
 * ChatSidebar — sidebar completa do chat com lista de contatos.
 * Composição: ContactSearchBar + ContactFilters + scroll list de ContactListItem.
 *
 * Wireframe idêntico ao original (extraído de Chat.tsx); rebrand DS (var(--sminy-primary)).
 */

import React, { useEffect } from 'react';
import { conversationKey } from '../../utils/conversationIdentity.ts';
import {
  ChevronLeft,
  ChevronRight,
  Archive,
  Headphones,
  Loader2,
  MessageSquare,
} from 'lucide-react';
import { type ContactFilters as ContactFiltersState } from '../ContactFilters.tsx';
import ContactSearchBar from './ContactSearchBar.tsx';
import ContactListItem from './ContactListItem.tsx';
import type { Contact } from './types.ts';
import type { ContactPresenceActivity } from './ContactPresence.tsx';

interface ChatSidebarProps {
  // Display flags
  assignmentFilter?: AssignmentFilter;
  onAssignmentFilterChange?: (value: AssignmentFilter) => void;
  attendanceLoading?: boolean;
  attendanceEnabled?: boolean;
  conversationState?: ConversationState;
  onConversationStateChange?: (value: ConversationState) => void;
  showMobileChat: boolean;
  showArchivedSection: boolean;
  isContactScopeRestricted: boolean;

  // Search/filter state
  searchTerm: string;
  onSearchChange: (s: string) => void;
  showUnreadOnly: boolean;
  onToggleUnread: (v: boolean) => void;
  totalUnreadCount: number;
  contactFilters: ContactFiltersState;
  onFiltersChange: (f: ContactFiltersState) => void;

  // Contacts data
  contacts: Contact[];
  selectedConversationKey?: string | null;
  selectedContactPhone?: string | null;
  selectedContactPresenceActivity?: ContactPresenceActivity;
  isContactsLoading: boolean;
  isLoadingMoreContacts: boolean;
  hasMoreContacts: boolean;
  isThrottled: boolean;
  currentOffset: number;
  pageSize: number;

  // Helpers
  formatTimestamp: (ts: number) => string;

  // Actions
  onNewChat: () => void;
  onSelectContact: (c: Contact) => void;
  onArchiveToggle: (c: Contact, isArchived: boolean) => void;
  isArchiving: boolean;
  onEnterArchivedSection: () => void;
  onLeaveArchivedSection: () => void;
  onTaskClick: (c: Contact) => void;
  onLoadMore: (offset: number, pageSize: number, append: boolean) => void;
  setIsThrottled: (v: boolean) => void;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  attendanceLoading = false,
  attendanceEnabled = false,
  assignmentFilter,
  onAssignmentFilterChange,
  conversationState,
  onConversationStateChange,
  showMobileChat,
  showArchivedSection,
  isContactScopeRestricted,
  searchTerm,
  onSearchChange,
  showUnreadOnly,
  onToggleUnread,
  totalUnreadCount,
  contactFilters,
  onFiltersChange,
  contacts,
  selectedContactPhone,
  selectedConversationKey,
  selectedContactPresenceActivity = null,
  isContactsLoading,
  isLoadingMoreContacts,
  hasMoreContacts,
  isThrottled,
  currentOffset,
  pageSize,
  formatTimestamp,
  onNewChat,
  onSelectContact,
  onArchiveToggle,
  isArchiving,
  onEnterArchivedSection,
  onLeaveArchivedSection,
  onTaskClick,
  onLoadMore,
  setIsThrottled,
}) => {
  const numericSearchDigits = searchTerm.replace(/\D/g, '');
  const isNumericSearch =
    searchTerm.trim().length > 0 &&
    numericSearchDigits.length > 0 &&
    !/[A-Za-zÀ-ÿ]/.test(searchTerm);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 200;

    if (nearBottom && hasMoreContacts && !isLoadingMoreContacts && !isThrottled) {
      setIsThrottled(true);
      setTimeout(() => setIsThrottled(false), 1000);
      onLoadMore(currentOffset, pageSize, true);
    }
  };

  useEffect(() => {
    if (conversationState && showUnreadOnly) onToggleUnread(false);
  }, [conversationState, showUnreadOnly, onToggleUnread]);

  return (
    <aside
      className={`w-full lg:w-80 flex-shrink-0 border-r border-gray-200 flex flex-col bg-white overflow-hidden lg:flex ${
        showMobileChat ? 'hidden' : 'flex'
      }`}
    >
      <ContactSearchBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        showUnreadOnly={showUnreadOnly}
        onToggleUnread={onToggleUnread}
        unreadCount={totalUnreadCount}
        onNewChat={onNewChat}
        filterControl={<ChatFilterControl filters={contactFilters} onChange={onFiltersChange} assignment={assignmentFilter} onAssignmentChange={conversationState ? onAssignmentFilterChange : undefined} showStages={!showArchivedSection && !isContactScopeRestricted} />}
        filterChips={<ChatFilterChips filters={contactFilters} onChange={onFiltersChange} assignment={assignmentFilter} onAssignmentChange={conversationState ? onAssignmentFilterChange : undefined} showStages={!showArchivedSection && !isContactScopeRestricted} />}
        conversationTabs={conversationState && onConversationStateChange ? <ConversationTabs loading={attendanceLoading} assignment={assignmentFilter} value={conversationState} onChange={onConversationStateChange} /> : undefined}
      />

      {isContactScopeRestricted && !showArchivedSection && (
        <div className="mx-4 mb-3 p-3 bg-purple-50 border border-purple-200 rounded-xl">
          <div className="flex items-center gap-2">
            <Headphones className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">
              Escopo da equipe aplicado à lista de contatos
            </span>
          </div>
        </div>
      )}


      {/* Lista (scroll) */}
      <div className="flex-1 overflow-y-auto min-h-0" onScroll={handleScroll}>
        <div className="p-3">
          {/* Toggle arquivadas */}
          {!conversationState && (!showArchivedSection ? (
            <button
              onClick={onEnterArchivedSection}
              className="group w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-gray-600 hover:bg-sminy-primary/5 hover:text-sminy-primary transition-colors mb-2"
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <Archive className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium">Arquivadas</span>
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-sminy-primary group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </button>
          ) : (
            <button
              onClick={onLeaveArchivedSection}
              className="group w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors mb-2 border-b border-gray-100"
            >
              <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              <span className="text-sm font-medium">Voltar — Arquivadas</span>
            </button>
          ))}

          {/* Loading skeletons */}
          {isContactsLoading ? (
            <div className="space-y-2">
              {Array(8)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="w-full p-3 rounded-xl flex items-start space-x-3 animate-pulse"
                  >
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                    </div>
                    <div className="h-3 bg-gray-200 rounded w-10" />
                  </div>
                ))}
            </div>
          ) : contacts.length === 0 ? (
            // Empty state DS
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800 mb-1">
                Nenhuma conversa encontrada
              </h3>
              <p className="text-xs text-gray-500 max-w-xs">
                Ajuste os filtros ou inicie um novo chat.
              </p>
            </div>
          ) : (
            contacts.map((contact) => {
              const key = conversationKey(contact);
              const isSelected = !!key && (selectedConversationKey ?? selectedContactPhone) === key;
              return (
                <ContactListItem
                  key={key}
                  contact={contact}
                  attendanceEnabled={attendanceEnabled}
                  isSelected={isSelected}
                  isTyping={isSelected && selectedContactPresenceActivity === 'typing'}
                  isArchivedView={showArchivedSection}
                  isArchiving={isArchiving}
                  showPhone={isNumericSearch || contact.possible_duplicate === true}
                  displayedTimestamp={formatTimestamp(contact.timestampNumber)}
                  onSelect={() => onSelectContact(contact)}
                  onArchiveToggle={() => onArchiveToggle(contact, showArchivedSection)}
                  onTaskClick={() => onTaskClick(contact)}
                />
              );
            })
          )}

          {/* Loading more */}
          {isLoadingMoreContacts && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-sminy-primary" />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default ChatSidebar;
